import React, { useState, useEffect } from 'react';
import { Shield, TrendingUp, Phone, Mail, Calendar, Filter, Download, Eye, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Authentication - Password from environment variable
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "proteccapital2026";

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, contacted, resolved

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Acceso concedido');
      fetchData();
    } else {
      toast.error('Contraseña incorrecta');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, contactsRes] = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/contact`)
      ]);
      setQuotes(quotesRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (type, id, newStatus) => {
    try {
      await axios.patch(`${API}/${type}/${id}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      contacted: 'bg-blue-100 text-blue-800 border-blue-300',
      resolved: 'bg-green-100 text-green-800 border-green-300'
    };
    const labels = {
      pending: 'Pendiente',
      contacted: 'Contactado',
      resolved: 'Resuelto'
    };
    return (
      <Badge className={`${variants[status]} border`}>
        {labels[status]}
      </Badge>
    );
  };

  const filterData = (data) => {
    if (filter === 'all') return data;
    return data.filter(item => item.status === filter);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1565c0]/10 via-white to-[#00c853]/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center bg-gradient-to-r from-[#1565c0] to-[#00c853] text-white rounded-t-lg">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#1565c0]" />
            </div>
            <CardTitle className="text-2xl">Panel de Administración</CardTitle>
            <p className="text-sm opacity-90">ProtecCapital</p>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1565c0] focus:border-transparent"
                  placeholder="Ingresa la contraseña"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#1565c0] to-[#00c853] hover:from-[#1e88e5] hover:to-[#43a047] py-6"
              >
                Ingresar
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1565c0] to-[#00c853] text-white p-6 shadow-lg">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Panel de Administración</h1>
              <p className="opacity-90">ProtecCapital - Gestión de Leads</p>
            </div>
            <Button
              onClick={fetchData}
              variant="outline"
              className="bg-white text-[#1565c0] hover:bg-gray-100"
            >
              Actualizar Datos
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Cotizaciones</p>
                  <p className="text-3xl font-bold text-[#1565c0]">{quotes.length}</p>
                </div>
                <div className="w-12 h-12 bg-[#1565c0]/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#1565c0]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Contactos</p>
                  <p className="text-3xl font-bold text-[#00c853]">{contacts.length}</p>
                </div>
                <div className="w-12 h-12 bg-[#00c853]/10 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#00c853]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pendientes</p>
                  <p className="text-3xl font-bold text-yellow-600">
                    {[...quotes, ...contacts].filter(i => i.status === 'pending').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Resueltos</p>
                  <p className="text-3xl font-bold text-green-600">
                    {[...quotes, ...contacts].filter(i => i.status === 'resolved').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-[#1565c0]' : ''}
          >
            Todos
          </Button>
          <Button
            onClick={() => setFilter('pending')}
            variant={filter === 'pending' ? 'default' : 'outline'}
            className={filter === 'pending' ? 'bg-yellow-500' : ''}
          >
            Pendientes
          </Button>
          <Button
            onClick={() => setFilter('contacted')}
            variant={filter === 'contacted' ? 'default' : 'outline'}
            className={filter === 'contacted' ? 'bg-blue-500' : ''}
          >
            Contactados
          </Button>
          <Button
            onClick={() => setFilter('resolved')}
            variant={filter === 'resolved' ? 'default' : 'outline'}
            className={filter === 'resolved' ? 'bg-green-500' : ''}
          >
            Resueltos
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="quotes">Cotizaciones ({quotes.length})</TabsTrigger>
            <TabsTrigger value="contacts">Contactos ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes">
            <div className="grid gap-4">
              {filterData(quotes).map((quote) => (
                <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-[#0d1b2a]">{quote.name}</h3>
                          {getStatusBadge(quote.status)}
                        </div>
                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{quote.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="w-4 h-4" />
                            <span>{quote.phone}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Tipo de Servicio</p>
                        <p className="font-semibold text-[#1565c0] capitalize">{quote.service_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Categoría</p>
                        <p className="font-semibold text-[#00c853] capitalize">
                          {quote.service_category.replace('_', ' ')}
                        </p>
                      </div>
                    </div>

                    {quote.additional_info && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg border-l-4 border-[#1565c0]">
                        <p className="text-sm text-gray-600 mb-1">Información Adicional:</p>
                        <p className="text-gray-800">{quote.additional_info}</p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      {quote.status === 'pending' && (
                        <Button
                          onClick={() => updateStatus('quotes', quote.id, 'contacted')}
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          Marcar como Contactado
                        </Button>
                      )}
                      {quote.status === 'contacted' && (
                        <Button
                          onClick={() => updateStatus('quotes', quote.id, 'resolved')}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                        >
                          Marcar como Resuelto
                        </Button>
                      )}
                      <Button
                        onClick={() => window.open(`https://wa.me/57${quote.phone}`, '_blank')}
                        size="sm"
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filterData(quotes).length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500">
                    No hay cotizaciones con este filtro
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="grid gap-4">
              {filterData(contacts).map((contact) => (
                <Card key={contact.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-[#0d1b2a]">{contact.name}</h3>
                          {getStatusBadge(contact.status)}
                        </div>
                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{contact.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="w-4 h-4" />
                            <span>{contact.phone}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Interés:</p>
                      <p className="font-semibold text-[#1565c0] capitalize">{contact.service_type}</p>
                    </div>

                    <div className="mb-4 p-4 bg-green-50 rounded-lg border-l-4 border-[#00c853]">
                      <p className="text-sm text-gray-600 mb-1">Mensaje:</p>
                      <p className="text-gray-800">{contact.message}</p>
                    </div>

                    <div className="flex gap-2">
                      {contact.status === 'pending' && (
                        <Button
                          onClick={() => updateStatus('contact', contact.id, 'contacted')}
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          Marcar como Contactado
                        </Button>
                      )}
                      {contact.status === 'contacted' && (
                        <Button
                          onClick={() => updateStatus('contact', contact.id, 'resolved')}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                        >
                          Marcar como Resuelto
                        </Button>
                      )}
                      <Button
                        onClick={() => window.open(`https://wa.me/57${contact.phone}`, '_blank')}
                        size="sm"
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filterData(contacts).length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center text-gray-500">
                    No hay contactos con este filtro
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
